import React, { useState } from "react";
import { Loader2, ChevronDown, ChevronUp, Lightbulb, Copy, Check } from "lucide-react";
import toast from "react-hot-toast";
import api from "../../utils/axios";

/**
 * Contextual AI assistant that sits inside service creation modal tabs.
 *
 * Props:
 *  - serviceType: "venue" | "photography" | "caterers" | "decors"
 *  - step: "name" | "description" | "amenities" | "pricing" | "photos"
 *  - formData: current form state
 *  - onApply: (result) => void — called when vendor clicks "Apply"
 *  - compact: boolean — minimal collapsed mode (default true)
 */
export default function ServiceAIAssistant({ serviceType, step, formData, onApply, compact = true }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [copied, setCopied] = useState(null);

  const handleGenerate = async () => {
    try {
      setLoading(true);
      const { data } = await api.post("/api/vendor/ai/service-assist", {
        serviceType,
        step,
        formData,
        prompt: prompt.trim() || undefined,
      });
      setResult(data.data);
      setOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.message || "AI suggestion failed");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };

  const stepLabels = {
    name: "Suggest names",
    description: "Write description",
    amenities: "Suggest amenities",
    pricing: "Research pricing",
    photos: "Photo ideas",
  };

  /* ── Collapsed pill ─────────────────────────────────────────────────── */
  if (compact && !open && !result) {
    return (
      <button
        type="button"
        onClick={handleGenerate}
        disabled={loading}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-50 border border-purple-200 text-xs font-semibold text-purple-700 hover:bg-purple-100 transition disabled:opacity-50"
      >
        {loading ? <Loader2 size={13} className="animate-spin" /> : <Lightbulb size={13} />}
        {loading ? "Thinking..." : `AI: ${stepLabels[step] || "Suggest"}`}
      </button>
    );
  }

  /* ── Expanded panel ─────────────────────────────────────────────────── */
  return (
    <div className="rounded-xl border border-purple-200 bg-gradient-to-br from-purple-50 via-white to-pink-50 overflow-hidden">
      {/* Header */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-purple-50/50 transition"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-purple-800">
          <Lightbulb size={15} className="text-purple-600" />
          VendorLift — {stepLabels[step] || "AI Assist"}
        </span>
        {open ? <ChevronUp size={16} className="text-purple-400" /> : <ChevronDown size={16} className="text-purple-400" />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          {/* Optional prompt */}
          <div className="flex gap-2">
            <input
              type="text"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={step === "name" ? "e.g. royal, modern, garden theme..." : "Any specific instructions..."}
              className="flex-1 px-3 py-2 text-sm border border-purple-200 rounded-lg bg-white outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400"
            />
            <button
              type="button"
              onClick={handleGenerate}
              disabled={loading}
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-medium shadow-md shadow-purple-600/20 hover:shadow-lg transition-all disabled:opacity-50 flex items-center gap-1.5"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Lightbulb size={14} />}
              {loading ? "..." : "Generate"}
            </button>
          </div>

          {/* ── Results ──────────────────────────────────────────────── */}

          {/* Name suggestions */}
          {step === "name" && result?.suggestions && (
            <div className="space-y-1.5">
              {result.suggestions.map((s, i) => (
                <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-white border border-gray-100 hover:border-purple-200 transition group">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{s.name}</p>
                    {s.tagline && <p className="text-xs text-gray-500">{s.tagline}</p>}
                  </div>
                  <button
                    type="button"
                    onClick={() => { onApply?.({ name: s.name }); toast.success("Name applied"); }}
                    className="px-2.5 py-1 rounded-md bg-purple-100 text-purple-700 text-xs font-medium opacity-0 group-hover:opacity-100 transition"
                  >
                    Use
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Description */}
          {step === "description" && result?.descriptionHtml && (
            <div className="space-y-2">
              <div className="p-3 rounded-lg bg-white border border-gray-100">
                <div className="text-sm text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: result.descriptionHtml }} />
              </div>
              {result.seoKeywords?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {result.seoKeywords.map((kw, i) => (
                    <span key={i} className="px-2 py-0.5 bg-purple-50 text-purple-600 text-[11px] font-medium rounded-full">{kw}</span>
                  ))}
                </div>
              )}
              <button
                type="button"
                onClick={() => { onApply?.({ description: result.descriptionHtml }); toast.success("Description applied"); }}
                className="w-full py-2 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-medium shadow-md shadow-purple-600/20 hover:shadow-lg transition-all"
              >
                Apply Description
              </button>
            </div>
          )}

          {/* Amenities */}
          {step === "amenities" && result && (
            <div className="space-y-2">
              {result.mustHave?.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Must-have</p>
                  <div className="flex flex-wrap gap-1.5">
                    {result.mustHave.map((a, i) => (
                      <button key={i} type="button" onClick={() => { onApply?.({ amenity: a }); toast.success(`${a} added`); }}
                        className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-200 hover:bg-emerald-100 transition">
                        + {a.replace(/_/g, " ")}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {result.suggested?.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Suggested</p>
                  <div className="flex flex-wrap gap-1.5">
                    {result.suggested.map((a, i) => (
                      <button key={i} type="button" onClick={() => { onApply?.({ amenity: a }); toast.success(`${a} added`); }}
                        className="px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 text-xs font-medium border border-purple-200 hover:bg-purple-100 transition">
                        + {a.replace(/_/g, " ")}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {result.tip && (
                <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
                  <Lightbulb size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-800">{result.tip}</p>
                </div>
              )}
            </div>
          )}

          {/* Pricing */}
          {step === "pricing" && result && (
            <div className="space-y-2">
              {result.suggestion?.marketRange && (
                <div className="grid grid-cols-3 gap-2">
                  {["low", "mid", "high"].map((tier) => (
                    <div key={tier} className="p-2.5 rounded-lg bg-white border border-gray-100 text-center">
                      <p className="text-[10px] font-bold text-gray-400 uppercase">{tier === "mid" ? "Market Avg" : tier}</p>
                      <p className="text-lg font-bold text-gray-800">₹{(result.suggestion.marketRange[tier] || 0).toLocaleString("en-IN")}</p>
                    </div>
                  ))}
                </div>
              )}
              {result.competitors?.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Competitors</p>
                  {result.competitors.slice(0, 4).map((c, i) => (
                    <div key={i} className="flex justify-between py-1.5 text-xs text-gray-600 border-b border-gray-50 last:border-0">
                      <span>{c.name}</span>
                      <span className="font-semibold">{c.price}</span>
                    </div>
                  ))}
                </div>
              )}
              {result.tip && (
                <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
                  <Lightbulb size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-800">{result.tip}</p>
                </div>
              )}
            </div>
          )}

          {/* Photos */}
          {step === "photos" && result && (
            <div className="space-y-2">
              {result.shotList?.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Shot list</p>
                  <ul className="space-y-1">
                    {result.shotList.map((shot, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs text-gray-700">
                        <span className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                        {shot}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result.photoTips?.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Tips</p>
                  {result.photoTips.map((tip, i) => (
                    <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-white border border-gray-100 mb-1">
                      <Lightbulb size={12} className="text-amber-500 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-gray-700">{tip}</p>
                    </div>
                  ))}
                </div>
              )}
              {result.searchQueries?.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Search for inspiration</p>
                  <div className="flex flex-wrap gap-1.5">
                    {result.searchQueries.map((q, i) => (
                      <a key={i} href={`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(q.query || q.label)}`}
                        target="_blank" rel="noopener noreferrer"
                        className="px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 text-xs font-medium border border-purple-200 hover:bg-purple-100 transition">
                        {q.label}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
